import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/utils.js';
import { Spinner } from './Spinner.js';

/**
 * Design-system button (audit P0). Replaces the hand-rolled variant map.
 *
 * Variant notes:
 *  - primary   — accent fill; THE action on a screen (one per view).
 *  - secondary — neutral raised surface; the calm companion to primary.
 *  - outline   — bordered, transparent fill; table/toolbars.
 *  - ghost     — transparent until hover; low-emphasis actions. (The old
 *                ghost was an underlined sky link on a white pill — audit F1.)
 *  - destructive — irreversible actions.
 *  - link      — inline text links only, never as a standalone CTA.
 */
const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 rounded-xl text-center',
    'font-display font-semibold tracking-wide',
    'transition-[background-color,border-color,box-shadow,transform] duration-150',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
    'active:scale-[0.98]',
    'disabled:pointer-events-none disabled:opacity-45',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-fg shadow-sm hover:bg-accent-hover',
        secondary:
          'border border-border bg-surface-raised text-fg shadow-sm hover:bg-surface dark:hover:bg-surface-raised/60',
        outline: 'border border-border bg-transparent text-fg hover:bg-accent-soft',
        ghost: 'bg-transparent text-fg hover:bg-accent-soft',
        destructive: 'bg-red-600 text-white shadow-sm hover:bg-red-700',
        link: 'text-accent underline-offset-4 hover:underline rounded-md px-0.5',
      },
      size: {
        // Sizes grow with the viewport: a phone gets comfortable thumb targets, a desktop gets bigger labels.
        sm: 'h-12 px-4 text-base lg:h-12 lg:px-5 lg:text-lg',
        md: 'h-12 px-6 text-lg lg:h-14 lg:px-7 lg:text-xl',
        lg: 'h-14 px-7 text-xl lg:h-16 lg:px-9 lg:text-2xl',
        xl: 'h-16 px-9 text-2xl lg:h-[4.5rem] lg:px-12 lg:text-3xl',
        icon: 'h-12 w-12',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Shows a spinner and disables interaction while true. */
  readonly loading?: boolean;
  readonly children?: ReactNode;
}

export function Button({
  variant,
  size,
  loading = false,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner className="size-4" /> : null}
      {children}
    </button>
  );
}

export { buttonVariants };
